import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  type ChartData,
  type ChartOptions,
} from "chart.js";
// Side-effect import: registers the date-fns adapter used by the time scale.
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";
import { Skeleton } from "@libertify/ui";
import { analyticsTheme } from "./analyticsTheme";
import { AnalyticsCard } from "./AnalyticsCard";
import {
  getSharedPlugins,
  getGridAxis,
  getCountAxis,
  getTimeAxis,
  getTimestampAxis,
} from "./utils/";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

export interface DatasetConfig {
  label: string;
  data: number[];
  color: string;
}

export interface AnalyticsLineChartProps {
  title: string;
  subtitle: string;
  labels: string[];
  datasets: DatasetConfig[];
  loading?: boolean;
  height?: number;
  /**
   * Fixed plot height, legend and axis included. The card then grows around
   * it instead of taking its height from its container, and still stretches
   * to a taller row.
   */
  plotHeight?: number;
  chartOptions?: Partial<ChartOptions<"line">>;
  /**
   * X-axis scale. "category" (default) spaces points by index; "time" spaces
   * them proportionally to elapsed time — labels must then be date values;
   * "linear" spaces them by numeric x values supplied via `xValues`.
   */
  xScaleType?: "category" | "time" | "linear";
  /**
   * Numeric x-coordinate for each point
   */
  xValues?: number[];
  /**
   * Upper bound of a `"linear"` x-axis.  When set, the axis ends exactly here rather than auto-fitting to the data.
   */
  xMax?: number;
}

const getXAxis = (
  xScaleType: NonNullable<AnalyticsLineChartProps["xScaleType"]>,
  xMax?: number
) => {
  switch (xScaleType) {
    case "time":
      return getTimeAxis();
    case "linear":
      return getTimestampAxis(xMax);
    default:
      return getGridAxis();
  }
};

export const AnalyticsLineChart: React.FC<AnalyticsLineChartProps> = ({
  title,
  subtitle,
  labels,
  datasets,
  loading = false,
  height = 300,
  plotHeight,
  chartOptions,
  xScaleType = "category",
  xValues,
  xMax,
}) => {
  const defaultOptions = useMemo<ChartOptions<"line">>(() => {
    const xAxis = getXAxis(xScaleType, xMax);
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: getSharedPlugins(),
      scales: {
        x: xAxis,
        y: getCountAxis(),
      },
    };
  }, [xScaleType, xMax]);

  const chartData = useMemo<ChartData<"line">>(
    () => ({
      labels,
      datasets: datasets.map((ds) => ({
        label: ds.label,
        // xValues, labels and datasets are all derived from the same points
        // array (co-indexed by construction), so xValues[i] always exists here;
        // the ?? 0 only satisfies the type and can't fire in practice.
        data: xValues
          ? ds.data.map((y, i) => ({ x: xValues[i] ?? 0, y }))
          : ds.data,
        borderColor: ds.color,
        backgroundColor: `${ds.color}0D`,
        pointBackgroundColor: ds.color,
        pointBorderColor: analyticsTheme.pointBorder,
        pointBorderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: true,
        clip: false,
      })),
    }),
    [labels, datasets, xValues]
  );

  const mergedOptions = useMemo<ChartOptions<"line">>(() => {
    if (!chartOptions) return defaultOptions;
    return {
      ...defaultOptions,
      ...chartOptions,
      plugins: {
        ...defaultOptions.plugins,
        ...chartOptions.plugins,
      },
      scales: {
        ...defaultOptions.scales,
        ...chartOptions.scales,
      },
    };
  }, [defaultOptions, chartOptions]);

  if (loading) {
    return (
      <AnalyticsCard title={title} subtitle={subtitle} loading>
        <Skeleton variant="rectangular" width="100%" height={plotHeight ?? height} />
      </AnalyticsCard>
    );
  }

  return (
    <AnalyticsCard title={title} subtitle={subtitle}>
      <div
        style={
          plotHeight == null
            ? { flex: 1, maxHeight: height }
            : { flex: "1 0 auto", height: plotHeight, position: "relative" }
        }
      >
        <Line data={chartData} options={mergedOptions} />
      </div>
    </AnalyticsCard>
  );
};
